import { useNavigate } from 'react-router-dom';
import { useAdmin } from './AdminContext';
import { REALMS } from '../../../../shared/src/realms';

export function Dashboard() {
  const { realmStats, totalCount, reviewCount, isRefreshing, refreshStats } = useAdmin();
  const navigate = useNavigate();

  const activeRealms = Object.keys(realmStats).filter(k => realmStats[k].total > 0).length;
  const totalRealms = Object.keys(REALMS).length;

  // Calculate overall difficulty distribution
  let totalEasy = 0, totalMedium = 0, totalHard = 0;
  Object.values(realmStats).forEach(s => {
    totalEasy += s.difficulties.easy;
    totalMedium += s.difficulties.medium;
    totalHard += s.difficulties.hard;
  });

  // Find max realm count for bar scaling
  const maxRealmCount = Math.max(1, ...Object.values(realmStats).map(s => s.total));

  return (
    <div>
      {/* ── KPI Row ── */}
      <div className="admin-kpi-grid">
        <div className="admin-kpi">
          <span className="admin-kpi-label">Total Questions</span>
          <span className="admin-kpi-value" style={{ color: '#c4973a' }}>{totalCount.toLocaleString()}</span>
          <span className="admin-kpi-sub">across all realms</span>
        </div>
        <div className="admin-kpi">
          <span className="admin-kpi-label">Active Realms</span>
          <span className="admin-kpi-value" style={{ color: '#14b8a6' }}>{activeRealms} / {totalRealms}</span>
          <span className="admin-kpi-sub">{totalRealms - activeRealms === 0 ? 'all active' : `${totalRealms - activeRealms} empty`}</span>
        </div>
        <div className="admin-kpi">
          <span className="admin-kpi-label">Difficulty Mix</span>
          <span className="admin-kpi-value" style={{ color: '#818cf8', fontSize: '1.4rem' }}>
            {totalCount > 0 ? Math.round(totalEasy / totalCount * 100) : 0}% E
          </span>
          <span className="admin-kpi-sub">
            {Math.round(totalMedium / Math.max(1, totalCount) * 100)}% M · {Math.round(totalHard / Math.max(1, totalCount) * 100)}% H
          </span>
        </div>
        <div className="admin-kpi" style={{ cursor: reviewCount > 0 ? 'pointer' : 'default' }} onClick={() => reviewCount > 0 && navigate('/sys/wotgate/review')}>
          <span className="admin-kpi-label">Review Backlog</span>
          <span className="admin-kpi-value" style={{ color: reviewCount > 0 ? '#ef4444' : '#10b981' }}>
            {reviewCount > 0 ? reviewCount : '✓'}
          </span>
          <span className="admin-kpi-sub">{reviewCount > 0 ? 'pending review' : 'all clear'}</span>
        </div>
      </div>

      {/* ── Realm Distribution ── */}
      <div className="admin-card" style={{ marginBottom: '24px' }}>
        <div className="admin-card-header">
          <h3 className="admin-card-title">Realm Distribution</h3>
          <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={refreshStats} disabled={isRefreshing}>
            {isRefreshing ? '...' : '↻ REFRESH'}
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {Object.entries(REALMS).map(([key, val]) => {
            const stats = realmStats[key];
            const total = stats?.total || 0;
            const pct = (total / maxRealmCount) * 100;

            return (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
                onClick={() => navigate(`/sys/wotgate/database?realm=${key}`)}>
                <div style={{ width: '100px', fontSize: '0.85rem', fontFamily: 'var(--font-heading)', color: val.color, letterSpacing: '0.05em' }}>
                  {key}
                </div>
                <div style={{ flex: 1, height: '24px', background: '#16223A', borderRadius: '4px', overflow: 'hidden', position: 'relative' }}>
                  {/* Stacked difficulty bar */}
                  <div style={{ display: 'flex', height: '100%', width: `${pct}%`, transition: 'width 0.5s ease' }}>
                    {stats && total > 0 && (
                      <>
                        <div style={{ width: `${(stats.difficulties.easy / total) * 100}%`, background: '#10b981' }} />
                        <div style={{ width: `${(stats.difficulties.medium / total) * 100}%`, background: '#f59e0b' }} />
                        <div style={{ width: `${(stats.difficulties.hard / total) * 100}%`, background: '#ef4444' }} />
                      </>
                    )}
                  </div>
                </div>
                <div style={{ width: '60px', textAlign: 'right', fontSize: '0.8rem', fontFamily: 'var(--font-mono)', color: total > 0 ? '#ccccd4' : '#3E4E70' }}>
                  {total}
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', gap: '16px', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #16223A' }}>
          <span style={{ fontSize: '0.8rem', fontFamily: 'var(--font-mono)', color: '#10b981' }}>● Easy</span>
          <span style={{ fontSize: '0.8rem', fontFamily: 'var(--font-mono)', color: '#f59e0b' }}>● Medium</span>
          <span style={{ fontSize: '0.8rem', fontFamily: 'var(--font-mono)', color: '#ef4444' }}>● Hard</span>
        </div>
      </div>

      {/* ── Quick Actions ── */}
      <div className="admin-card">
        <h3 className="admin-card-title" style={{ marginBottom: '16px' }}>Quick Actions</h3>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button className="admin-btn admin-btn-primary" onClick={() => navigate('/sys/wotgate/factory')}>
            🏭 Open Factory
          </button>
          <button className="admin-btn admin-btn-success" onClick={() => navigate('/sys/wotgate/review')}>
            ✅ Review Queue {reviewCount > 0 && `(${reviewCount})`}
          </button>
          <button className="admin-btn admin-btn-secondary" onClick={() => navigate('/sys/wotgate/coverage')}>
            🗺️ Coverage Map
          </button>
          <button className="admin-btn admin-btn-secondary" onClick={() => navigate('/sys/wotgate/analytics')}>
            📈 Analytics
          </button>
        </div>
      </div>
    </div>
  );
}
